<template>
    <div>
        <!-- Floating Sync Status Pill (Visible whenever there are pending items) -->
        <div v-if="pendingCount > 0" 
            class="fixed left-4 bottom-5 z-[999] flex items-center gap-2 bg-slate-900/90 dark:bg-gray-800/95 text-white px-3.5 py-2 rounded-full shadow-2xl border border-slate-700/80 backdrop-blur-md cursor-pointer hover:scale-105 transition-all duration-200"
            @click="isOpen = true"
            role="button"
            :title="isOnline ? $t('syncModal.pillReadyTitle') : $t('syncModal.pillOfflineTitle')">
            
            <div class="relative flex items-center justify-center">
                <span class="relative flex h-3 w-3">
                    <span :class="isOnline ? 'animate-ping bg-amber-400' : 'bg-gray-400'" class="absolute inline-flex h-full w-full rounded-full opacity-75"></span>
                    <span :class="isOnline ? 'bg-amber-500' : 'bg-gray-400'" class="relative inline-flex rounded-full h-3 w-3"></span>
                </span>
            </div>

            <span class="text-xs font-semibold tracking-wide">
                {{ pendingCount }} {{ pendingCount === 1 ? $t('syncModal.pillOne') : $t('syncModal.pillMany') }}
            </span>

            <ArrowPathIcon v-if="isSyncing" class="w-4 h-4 animate-spin text-indigo-400" />
            <CloudArrowUpIcon v-else class="w-4 h-4 text-gray-300" />
        </div>

        <!-- Sync Details Modal -->
        <div v-if="isOpen" 
            class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            @click.self="isOpen = false">
            
            <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full overflow-hidden flex flex-col max-h-[85vh] animate-in fade-in zoom-in-95 duration-150">
                
                <!-- Modal Header -->
                <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div class="flex items-center gap-3">
                        <div class="p-2 bg-indigo-50 dark:bg-indigo-950/50 rounded-xl text-indigo-600 dark:text-indigo-400">
                            <CloudArrowUpIcon class="w-6 h-6" />
                        </div>
                        <div>
                            <h3 class="text-base font-bold text-gray-900 dark:text-white">
                                {{ $t('syncModal.title') }}
                            </h3>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                                {{ isOnline ? $t('syncModal.onlineSubtitle') : $t('syncModal.offlineSubtitle') }}
                            </p>
                        </div>
                    </div>
                    <button @click="isOpen = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700">
                        <XMarkIcon class="w-5 h-5" />
                    </button>
                </div>

                <!-- Modal Body: List of pending items -->
                <div class="p-6 overflow-y-auto flex-1 space-y-3">
                    <div v-if="pendingItems.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                        <CheckCircleIcon class="w-12 h-12 mx-auto text-emerald-500 mb-2" />
                        <p class="font-medium text-sm">{{ $t('syncModal.emptyTitle') }}</p>
                        <p class="text-xs mt-1">{{ $t('syncModal.emptyDesc') }}</p>
                    </div>

                    <div v-for="item in pendingItems" :key="item.id" 
                        class="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-700">
                        <div class="flex items-center gap-3 min-w-0">
                            <div class="p-2 rounded-lg bg-white dark:bg-slate-800 text-indigo-500 shadow-sm shrink-0">
                                <DocumentTextIcon v-if="item.icon === 'note'" class="w-4 h-4" />
                                <PencilSquareIcon v-else-if="item.icon === 'pencil'" class="w-4 h-4" />
                                <MusicalNoteIcon v-else-if="item.icon === 'music'" class="w-4 h-4" />
                                <StarIcon v-else-if="item.icon === 'star'" class="w-4 h-4" />
                                <DocumentIcon v-else class="w-4 h-4" />
                            </div>
                            <div class="min-w-0">
                                <p class="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                                    {{ item.label }}
                                </p>
                                <p class="text-[10px] text-gray-400 truncate">
                                    {{ formatDate(item.timestamp) }}
                                </p>
                            </div>
                        </div>
                        <span class="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400">
                            {{ $t('syncModal.pendingBadge') }}
                        </span>
                    </div>
                </div>

                <!-- Modal Footer -->
                <div class="p-4 bg-gray-50 dark:bg-slate-800/80 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <span class="text-xs text-gray-500 dark:text-gray-400">
                        {{ pendingCount }} {{ pendingCount === 1 ? $t('syncModal.itemOne') : $t('syncModal.itemMany') }}
                    </span>
                    <button 
                        @click="triggerSync" 
                        :disabled="!isOnline || isSyncing || pendingCount === 0"
                        class="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-xl shadow transition-colors">
                        <ArrowPathIcon class="w-4 h-4" :class="{ 'animate-spin': isSyncing }" />
                        <span>{{ isSyncing ? $t('syncModal.syncing') : $t('syncModal.syncNow') }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted, onUnmounted } from 'vue';
import { 
    CloudArrowUpIcon, 
    ArrowPathIcon, 
    XMarkIcon, 
    CheckCircleIcon,
    DocumentTextIcon, 
    PencilSquareIcon, 
    MusicalNoteIcon, 
    StarIcon, 
    DocumentIcon 
} from '@heroicons/vue/24/outline';
import syncService from '@/service/sync.service';
import { appEvents } from '@/store/app.events';

const isOpen = ref(false);
const pendingCount = ref(0);
const pendingItems = ref([]);
const isOnline = ref(navigator.onLine);
const isSyncing = ref(false);

const updateState = () => {
    pendingCount.value = syncService.getPendingCount();
    pendingItems.value = syncService.getQueueDetails();
    isOnline.value = navigator.onLine;
};

const handleOnline = () => {
    isOnline.value = true;
};

const handleOffline = () => {
    isOnline.value = false;
};

const handleQueueChanged = () => {
    updateState();
};

const handleSyncCompleted = () => {
    isSyncing.value = false;
    updateState();
};

const triggerSync = async () => {
    if (!navigator.onLine || isSyncing.value) return;
    isSyncing.value = true;
    await syncService.processQueue();
    isSyncing.value = false;
    updateState();
};

const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

onMounted(() => {
    updateState();
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    appEvents.on('sync:queue-changed', handleQueueChanged);
    appEvents.on('sync:completed', handleSyncCompleted);
});

onUnmounted(() => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    appEvents.off('sync:queue-changed', handleQueueChanged);
    appEvents.off('sync:completed', handleSyncCompleted);
});
</script>
